import React, { useState, useEffect } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Switch,
  Button,
  Table,
  Statistic,
  message
} from "antd";
import { FileTextOutlined, DollarOutlined } from "@ant-design/icons";
import moment from "moment";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import billingApi from "../../billing/store/api";

const { RangePicker } = DatePicker;

// Create custom locale with Monday as first day of week
const mondayFirstLocale = {
  ...enUS,
  lang: {
    ...enUS.lang,
    weekStart: 1
  },
  DatePicker: {
    ...enUS.DatePicker,
    lang: {
      ...enUS.DatePicker.lang,
      weekStart: 1
    }
  }
};

// Custom RangePicker component with Monday-first week
const MondayFirstRangePicker = (props) => {
  const tempLocale = moment.localeData()._config;

  React.useEffect(() => {
    moment.updateLocale("en", {
      week: {
        dow: 1,
        doy: 4
      }
    });

    return () => {
      moment.updateLocale("en", tempLocale);
    };
  }, []);

  return (
    <ConfigProvider locale={mondayFirstLocale}>
      <RangePicker {...props} />
    </ConfigProvider>
  );
};

const GenerateSaasModal = ({ visible, onCancel }) => {
  const [invoiceDateRange, setInvoiceDateRange] = useState([
    moment().startOf("month"),
    moment().endOf("month")
  ]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [proRated, setProRated] = useState(true);
  const [emailClients, setEmailClients] = useState(true);
  const [loadingSaasItems, setLoadingSaasItems] = useState(false);
  const [loadingGeneration, setLoadingGeneration] = useState(false);
  const [saasData, setSaasData] = useState(null);

  useEffect(() => {
    if (visible) {
      handleLoadSaasItems();
    }
  }, [visible]);

  // Loaded without a client filter on purpose: the table is filtered client-side and
  // the Client Filter options are built from whatever this date range returned.
  const handleLoadSaasItems = async (dateRange = invoiceDateRange) => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingSaasItems(true);
    try {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      const response = await billingApi.getSaasItemsForInvoice(startDate, endDate);
      setSaasData(response);
    } catch (error) {
      console.error("Failed to load SaaS items:", error);
      message.error("Failed to load SaaS items");
    } finally {
      setLoadingSaasItems(false);
    }
  };

  const handleGenerateSaasInvoices = async () => {
    if (!invoiceDateRange || invoiceDateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingGeneration(true);
    try {
      const startDate = invoiceDateRange[0].format("YYYY-MM-DD");
      const endDate = invoiceDateRange[1].format("YYYY-MM-DD");
      const response = await billingApi.generateSaasInvoices(
        startDate,
        endDate,
        proRated,
        emailClients,
        selectedClient
      );

      const { generatedCount, emailedCount } = response;
      let successMessage = `Successfully generated ${generatedCount} invoice${
        generatedCount !== 1 ? "s" : ""
      }`;
      if (emailClients && emailedCount > 0) {
        successMessage += ` and emailed ${emailedCount} client${
          emailedCount !== 1 ? "s" : ""
        }`;
      }
      message.success(successMessage);

      // Close the modal after successful generation
      onCancel();
    } catch (error) {
      console.error("Failed to generate SaaS invoices:", error);
      message.error("Failed to generate invoices");
    } finally {
      setLoadingGeneration(false);
    }
  };

  // Filter items based on selected client
  const filteredItems = saasData?.items
    ? selectedClient
      ? saasData.items.filter((item) => item.clientId === selectedClient)
      : saasData.items
    : [];

  // Only the clients that actually have SaaS charges in this date range
  const clientsWithItems = React.useMemo(() => {
    const byId = new Map();
    (saasData?.items || []).forEach((item) => {
      if (item.clientId && !byId.has(item.clientId)) {
        byId.set(item.clientId, {
          id: item.clientId,
          clientName: item.clientName || "N/A"
        });
      }
    });
    return [...byId.values()].sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [saasData]);

  // Calculate filtered summary statistics
  const uniqueClients = new Set(filteredItems.map((item) => item.clientId)).size;
  const totalItems = filteredItems.length;
  const totalRevenue = filteredItems.reduce((sum, item) => sum + (item.cost || 0), 0);

  return (
    <Modal
      title="Generate SaaS Invoices"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width="90%"
      style={{ maxWidth: "1400px" }}
    >
      <div>
        Will generate invoices for all SaaS clients (clients with assigned tiers)
        including their tier charges and any active entitlements during the date range you
        choose.
      </div>

      {/* Filters Section */}
      <Card title="Filters" style={{ marginBottom: 16, marginTop: 16 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
            <MondayFirstRangePicker
              value={invoiceDateRange}
              onChange={(newRange) => {
                setInvoiceDateRange(newRange);
                // Refresh the SaaS items when date range changes
                if (newRange && newRange.length === 2) {
                  handleLoadSaasItems(newRange);
                }
              }}
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
            />
          </Col>

          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Client Filter</div>
            <Select
              style={{ width: "100%" }}
              placeholder="All clients"
              allowClear
              showSearch
              value={selectedClient}
              onChange={setSelectedClient}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {clientsWithItems.map((client) => (
                <Select.Option key={client.id} value={client.id}>
                  {client.clientName}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
          <Col xs={12} sm={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Pro-rated</div>
            <div style={{ display: "flex", alignItems: "center", height: 32 }}>
              <Switch
                checked={proRated}
                onChange={setProRated}
                style={{ marginRight: 8 }}
              />
              <span>{proRated ? "Pro-rated" : "Full period"}</span>
            </div>
          </Col>

          <Col xs={12} sm={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Email Notifications</div>
            <div style={{ display: "flex", alignItems: "center", height: 32 }}>
              <Switch
                checked={emailClients}
                onChange={setEmailClients}
                style={{ marginRight: 8 }}
              />
              <span>{emailClients ? "Email clients" : "No emails"}</span>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 8 }}>&nbsp;</div>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={handleGenerateSaasInvoices}
              loading={loadingGeneration}
              style={{ width: "100%", height: 40 }}
              size="large"
            >
              Generate Invoices
            </Button>
          </Col>
        </Row>
      </Card>

      {saasData && (
        <>
          {/* Summary Section */}
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Date Range"
                  value={saasData.summary.dateRange}
                  valueStyle={{ fontSize: 14, color: "#666" }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Total Clients"
                  value={uniqueClients}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Total Items"
                  value={totalItems}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Total Revenue (est.)"
                  value={totalRevenue}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
            </Row>
          </Card>

          {/* Items Table */}
          <Table
            dataSource={filteredItems}
            rowKey={(record) => `${record.clientId}-${record.itemId}-${record.type}`}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 800 }}
            loading={loadingSaasItems}
            columns={[
              {
                title: "Client",
                dataIndex: "clientName",
                width: 200,
                sorter: (a, b) => a.clientName.localeCompare(b.clientName)
              },
              {
                title: "Item",
                dataIndex: "itemName",
                width: 200,
                render: (value, record) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{value}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {record.type === "tier" ? "Tier" : "Entitlement"}
                    </div>
                  </div>
                )
              },
              {
                title: "Cost",
                dataIndex: "costDisplay",
                width: 150,
                align: "right",
                render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>
              }
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default GenerateSaasModal;
